import { RoleGate } from "@/common/components/layout/RoleGate";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { HatoColumnKey } from "@/features/accounts/api/accountsApi";
import { MoreVertical, Plus, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import type { HatoVisibleColumns } from "../hatoColumns";
import type { LifecycleState } from "../types";
import { AnimalSearchBar } from "./AnimalSearchBar";
import { HatoColumnsDropdown } from "./HatoColumnsDropdown";
import { HatoFilters, type SubStateFilterValue } from "./HatoFilters";

const ACTION_ROLES = ["owner", "admin", "operator"] as const;

type Props = {
  count: number | null;
  search: string;
  onSearch: (v: string) => void;
  // Props de filtros opcionales — la cabecera se reutiliza en el error state
  // donde no tiene sentido renderizar filtros sin data.
  accountId?: string;
  lifecycleFilter?: LifecycleState | null;
  onLifecycleFilterChange?: (value: LifecycleState | null) => void;
  lotFilter?: string | null;
  onLotFilterChange?: (value: string | null) => void;
  subStateFilter?: SubStateFilterValue | null;
  onSubStateFilterChange?: (value: SubStateFilterValue | null) => void;
  // columnas-hato: opcionales por la misma razón que los filtros — la cabecera
  // se reutiliza en el error state, donde configurar columnas no tiene sentido.
  columns?: HatoVisibleColumns;
  onColumnToggle?: (key: HatoColumnKey, visible: boolean) => void;
};

export function HatoListHeader({
  count,
  search,
  onSearch,
  accountId,
  lifecycleFilter,
  onLifecycleFilterChange,
  lotFilter,
  onLotFilterChange,
  subStateFilter,
  onSubStateFilterChange,
  columns,
  onColumnToggle,
}: Props) {
  const showFilters =
    accountId !== undefined &&
    onLifecycleFilterChange !== undefined &&
    onLotFilterChange !== undefined &&
    onSubStateFilterChange !== undefined;
  return (
    <header className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-medium text-3xl text-app-ink-1 tracking-tight">Hato</h1>
          {count !== null && (
            <p className="mt-1 text-app-ink-3 text-sm">
              {count} {count === 1 ? "animal" : "animales"}
            </p>
          )}
        </div>

        <RoleGate roles={[...ACTION_ROLES]}>
          <div
            className="hidden md:flex md:items-center md:gap-2"
            data-testid="hato-actions-desktop"
          >
            <Link
              to="/app/hato/import"
              className="inline-flex items-center gap-2 rounded-2xl border border-app-border bg-app-surface px-4 py-2.5 font-medium text-[14px] text-app-ink-1 transition-colors hover:bg-app-surface-2"
            >
              <Upload size={16} strokeWidth={2} />
              Importar hato
            </Link>
            <Link
              to="/app/hato/nuevo"
              className="inline-flex items-center gap-2 rounded-2xl bg-app-ink-1 px-4 py-2.5 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d]"
            >
              <Plus size={16} strokeWidth={2} />
              Registrar manual
            </Link>
          </div>

          <div className="md:hidden" data-testid="hato-actions-mobile">
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label="Acciones del hato"
                className="-mr-1 inline-flex size-9 items-center justify-center rounded-2xl text-app-ink-2 transition-colors hover:bg-app-surface-2"
              >
                <MoreVertical size={18} strokeWidth={2} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[12rem]">
                <DropdownMenuItem asChild>
                  <Link to="/app/hato/import" className="flex items-center gap-2">
                    <Upload size={16} strokeWidth={2} />
                    Importar hato
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/app/hato/nuevo" className="flex items-center gap-2">
                    <Plus size={16} strokeWidth={2} />
                    Registrar manual
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </RoleGate>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-3">
        <div className="w-full md:max-w-sm">
          <AnimalSearchBar value={search} onChange={onSearch} />
        </div>
        {showFilters && (
          <HatoFilters
            accountId={accountId}
            lifecycleFilter={lifecycleFilter ?? null}
            onLifecycleFilterChange={onLifecycleFilterChange}
            lotFilter={lotFilter ?? null}
            onLotFilterChange={onLotFilterChange}
            subStateFilter={subStateFilter ?? null}
            onSubStateFilterChange={onSubStateFilterChange}
          />
        )}
        {columns !== undefined && onColumnToggle !== undefined && (
          <HatoColumnsDropdown columns={columns} onToggle={onColumnToggle} />
        )}
      </div>
    </header>
  );
}
